import React, { useCallback, useMemo, useState } from "react";
import ReportFilterForm from "./reportFilterForm";
import { EnhancedTableColumn } from "fe-common/ui/table/enhancedTable";
import { SessionExchange } from "dialogue/src/graphql/types/graphql";
import { RagDao } from "dialogue/src/graphql/ragDao";
import { useClinicState } from "../../../clinic/clinicStateContext";
import { ClinicLoading } from "../uiTemplates/clinicLoading";
import { Alert, InputLabel, Link, MenuItem, styled } from "@mui/material";
import { ClinicDateTimePicker } from "../elements/clinicDateTimePicker";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { ReportTable } from "./reportTable";
import { openInDialogue } from "./utils";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { PrivatePlaceholder } from "fe-web-components/patientCard/PrivatePlaceholder";
import { AnnotatedText } from "fe-web-components/references/AnnotatedText";
import FeedbackDisplay from "./feedbackDisplay";
import ExportsDialog, { ExportFormat } from "./export/exportsDialog";
import { SessionType } from "dialogue/src/types/types";

const ExpandedRowStyle = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const SmallTitle = styled("div")({
  fontSize: "12px",
  fontWeight: "bold",
  marginBottom: "4px",
});

const FormHeader = styled("div")({
  display: "flex",
  alignItems: "start",
  justifyContent: "space-between",
  gap: 16,
});

const ClinicExchangesReport = () => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [reportData, setReportData] = useState<SessionExchange[]>(null);
  const [fromDate, setFromDate] = useState<string>(null);
  const [toDate, setToDate] = useState<string>(null);
  const [sessionType, setSessionType] = useState<SessionType | "ALL">("ALL");
  const [fileName, setFileName] = useState<string>("");

  const [
    {
      loading: clinicLoading,
      clinicData,
      rtlDisplay,
      clinicConfig,
      privateMode,
    },
  ] = useClinicState();

  const reportColumns: EnhancedTableColumn[] = useMemo(
    () => [
      {
        id: "sessionId",
        name: "Session ID",
        enableSort: true,
        render: (exchange: SessionExchange) => (
          <Link
            onClick={() =>
              openInDialogue(
                exchange.sessionId,
                clinicConfig[ClinicConfigKeys.RagApp],
              )
            }
          >
            {exchange.sessionId}
          </Link>
        ),
      },
      {
        id: "sessionDate",
        name: "Date Time",
        enableSort: true,
      },
      {
        id: "sessionUser",
        name: "User",
        enableSort: true,
        render: (exchange: SessionExchange) =>
          privateMode ? (
            <PrivatePlaceholder>Private information</PrivatePlaceholder>
          ) : (
            exchange.sessionUser
          ),
      },
      {
        id: "sessionType",
        name: "Type",
        enableSort: true,
      },
      {
        id: "index",
        name: "Index",
        enableSort: true,
      },
    ],
    [clinicConfig, privateMode],
  );

  const fetchData = useCallback(async () => {
    try {
      setErrorMsg("");
      const ragDao = new RagDao();
      const data = await ragDao.getClinicExchanges(
        clinicData.id,
        fromDate,
        toDate,
        sessionType === "ALL" ? undefined : sessionType,
      );

      setReportData(
        data.sort((a, b) => {
          let dateComparison =
            new Date(b.sessionDate).getTime() -
            new Date(a.sessionDate).getTime();
          if (dateComparison !== 0) return dateComparison;
          return a.index - b.index;
        }),
      );
    } catch (error) {
      console.error("Error fetching clinic exchanges:", error);
      setErrorMsg("Could not fetch clinic exchanges.");
    } finally {
      setLoading(false);
    }
  }, [clinicData.id, fromDate, toDate, sessionType]);

  const onSubmit = () => {
    // fetch data
    setLoading(true);
    const fDate = new Date(fromDate);
    const tDate = new Date(toDate);
    const fromMonth = fDate.getMonth() + 1;
    const toMonth = tDate.getMonth() + 1;
    const fromDay = fDate.getDate();
    const toDay = tDate.getDate();
    setFileName(
      `clinicExchanges_${clinicData?.name}_${fromDay}${
        fromMonth === toMonth ? "" : "_" + fromMonth
      }-${toDay}_${toMonth}`,
    );
    fetchData().finally(() => setLoading(false));
  };

  const dataToSpreadsheetFormat = (data: SessionExchange[]) => {
    if (!data) return [];
    const ragApp = clinicConfig[ClinicConfigKeys.RagApp];
    return data.map(({ sessionId, ...exchange }) => ({
      sessionId: `=HYPERLINK("https://dialogue.kahun.com/${
        ragApp ? "app/" + ragApp + "/" : ""
      }${sessionId}","${sessionId}")`,
      ...exchange,
    }));
  };

  const onReset = () => {
    setReportData(null);
  };

  const expandedRowContent = (exchange: SessionExchange) => {
    return (
      <ExpandedRowStyle>
        <div>
          <SmallTitle>Question</SmallTitle>
          {exchange.question}
        </div>
        <div>
          <SmallTitle>Answer</SmallTitle>
          <AnnotatedText
            text={exchange.answer}
            onFootnoteClick={null}
            onShowMore={null}
          />
        </div>
        {exchange.feedback && (
          <div>
            <SmallTitle>Feedback</SmallTitle>
            <FeedbackDisplay feedback={exchange.feedback} />
          </div>
        )}
      </ExpandedRowStyle>
    );
  };

  if (clinicLoading) return <ClinicLoading title={"Loading..."} />;

  return (
    <>
      <FormHeader>
        <ReportFilterForm
          onSubmit={onSubmit}
          onReset={onReset}
          disabled={clinicLoading || loading}
        >
          <ClinicDateTimePicker
            size="small"
            label="From"
            showTime={false}
            value={fromDate && new Date(fromDate)}
            onChange={(dateVal) => {
              setFromDate(dateVal?.toISOString());
            }}
            locale={clinicConfig.defaultLocale}
          />
          <ClinicDateTimePicker
            size="small"
            label="To"
            showTime={false}
            value={toDate && new Date(toDate)}
            onChange={(dateVal) => {
              setToDate(dateVal?.toISOString());
            }}
            locale={clinicConfig.defaultLocale}
          />
          <FormControl>
            <InputLabel id="sessionType-label">Type</InputLabel>
            <Select
              labelId="sessionType-label"
              id="sessionType-select"
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value as SessionType)}
              variant="outlined"
              size="small"
              label={"Type"}
              style={{ minWidth: 100 }}
            >
              <MenuItem value={"ALL"} key={"allTypes"}>
                ALL
              </MenuItem>
              {Object.keys(SessionType).map((key) => (
                <MenuItem value={SessionType[key]} key={"type" + key}>
                  {key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </ReportFilterForm>
        <ExportsDialog
          data={reportData?.map(({ feedback, ...exchange }) => ({
            ...exchange,
            feedback: JSON.stringify(feedback),
          }))}
          fileName={fileName}
          sheetName={"Exchanges"}
          formats={[ExportFormat.XLSX]}
          disabled={clinicLoading || loading || reportData === null}
        />
      </FormHeader>
      {loading && <ClinicLoading title={"Loading exchanges"} />}
      {errorMsg && (
        <Alert variant={"filled"} severity={"error"}>
          {errorMsg}
        </Alert>
      )}
      {!errorMsg && !loading && (
        <>
          <ReportTable
            reportData={reportData}
            columns={reportColumns}
            rtlView={rtlDisplay}
            expandedRowContent={expandedRowContent}
          />
        </>
      )}
    </>
  );
};

export default ClinicExchangesReport;
